import * as React from 'react';
import useAuth from 'hooks/useAuth';
import axios from '../../utils/axios';
import { cn } from '../../lib/utils';

/* ============================================================================
   Filtro de regional das listagens.

   Existia nas telas antigas de Grupos, Politicas e Usuarios (um Autocomplete na
   toolbar) e tinha ficado de fora da primeira rodada do visual novo. Para quem
   tem varias regionais, e o filtro que faz a lista caber na tela.

   Carrega SO as regionais do usuario logado (`/users/regionais/:codigo`) - nao a
   lista geral. O backend ja restringe o resultado das consultas as regionais do
   usuario; oferecer aqui uma regional a que ele nao tem acesso produziria uma
   lista vazia sem explicacao.

   "Todas" manda `regional=null`, que e o que os endpoints esperam para "sem
   filtro" (e nao `-1`, nem string vazia).

   ---------------------------------------------------------------------------
   VIROU BUSCA COM LUPA (19/08/2026, a pedido).

   Era um `<select>` nativo. Com 59 regionais de nomes parecidos - "Regional
   Araras", "Regional Araras" (repetido na base), "Regional Ceará", "Regional Ceará
   (Servis)" - a lista abre uma coluna alta que nao cabe na tela, e a unica forma de
   achar uma e rolar com o olho. O select nativo tem a busca por PRIMEIRA LETRA
   digitada, que nao serve quando quase tudo comeca com "Regional".

   O que este componente faz de diferente:
     - campo de texto com lupa: filtra por qualquer parte do nome ("bahia" acha
       "Regional Bahia") e tambem pelo codigo;
     - teclado completo: setas, Enter, Esc, e Tab fechando;
     - o valor escolhido aparece no campo quando fechado, entao o filtro ativo
       continua visivel sem abrir nada.

   NAO usei o `RHFAutoComplete`: ele depende de `useFormContext` (react-hook-form),
   e este filtro vive na barra da listagem, fora de qualquer formulario.
   ============================================================================ */

type Regional = { codigo: number; nome: string };

export default function FiltroRegional({
  valor,
  onChange,
}: {
  /** `null` = todas. */
  valor: number | null;
  onChange: (valor: number | null) => void;
}) {
  const { user } = useAuth();
  const [regionais, setRegionais] = React.useState<Regional[]>([]);

  const [aberto, setAberto] = React.useState(false);
  const [busca, setBusca] = React.useState('');
  const [indiceFoco, setIndiceFoco] = React.useState(0);
  const raiz = React.useRef<HTMLDivElement | null>(null);
  const campo = React.useRef<HTMLInputElement | null>(null);

  /* FALHA E FALHA, NAO "UMA REGIONAL SO" (28/09/2026).

     O `.catch` zerava a lista, e logo abaixo ha um `if (regionais.length <= 1)
     return null` legitimo - existe para quem tem uma regional so, onde filtrar
     nao faz sentido. Juntos, os dois faziam o filtro DESAPARECER da barra de
     Grupos, Politicas e Usuarios quando a consulta falhava, sem mensagem
     nenhuma.

     O estrago nao e a ausencia do filtro: e a conclusao que ela induz. Quem
     tem varias regionais ve a lista global sem o filtro, supoe que esta vendo
     so a sua, e le numero de outra regional como se fosse dele. Falha que se
     disfarca de decisao de produto e pior que erro na tela.

     Com `falhou`, o componente continua aparecendo e diz que nao conseguiu
     carregar - quem le sabe que o que esta vendo nao esta filtrado. */
  const [falhou, setFalhou] = React.useState(false);

  React.useEffect(() => {
    if (!user?.codigo) return;
    let vivo = true;
    axios
      .get<{ regionais: Regional[] }>(`/users/regionais/${user.codigo}`)
      .then((r) => {
        if (!vivo) return;
        setRegionais(r.data?.regionais ?? []);
        setFalhou(false);
      })
      .catch(() => {
        if (!vivo) return;
        setRegionais([]);
        setFalhou(true);
      });
    return () => {
      vivo = false;
    };
  }, [user?.codigo]);

  // Clique fora fecha. Sem isto a lista fica aberta atras do resto da tela.
  React.useEffect(() => {
    if (!aberto) return;
    const aoClicar = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', aoClicar);
    return () => document.removeEventListener('mousedown', aoClicar);
  }, [aberto]);

  const escolhida = React.useMemo(
    () => regionais.find((r) => Number(r.codigo) === Number(valor)) || null,
    [regionais, valor]
  );

  /* "Todas" e uma OPCAO da lista, e nao um botao separado: assim ela participa da
     navegacao por teclado e da posicao 0 - limpar o filtro e Enter na primeira
     linha, sem procurar outro controle. */
  const opcoes = React.useMemo(() => {
    const t = busca.trim().toLowerCase();
    const todas: Array<Regional | null> = [null, ...regionais];
    if (!t) return todas;
    return todas.filter((r) =>
      r === null
        ? 'todas as regionais'.includes(t)
        : r.nome?.toLowerCase().includes(t) || String(r.codigo).includes(t)
    );
  }, [regionais, busca]);

  React.useEffect(() => {
    setIndiceFoco(0);
  }, [busca, aberto]);

  /* Falha vem ANTES do "uma regional so": sem esta ordem, o erro seria lido
     como o caso normal de quem tem uma regional. Ver o comentario do efeito. */
  if (falhou)
    return (
      <span
        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-warn bg-warn-soft px-2.5 text-[11.5px] font-semibold text-warn"
        title="Nao foi possivel carregar suas regionais. A lista abaixo NAO esta filtrada por regional."
      >
        Regionais indisponíveis — lista sem filtro
      </span>
    );

  // Com uma regional so, o filtro nao filtra nada - some em vez de ocupar
  // espaco com uma escolha unica.
  if (regionais.length <= 1) return null;

  function escolher(r: Regional | null) {
    onChange(r ? Number(r.codigo) : null);
    setBusca('');
    setAberto(false);
  }

  function aoTeclar(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setAberto(false);
      return;
    }
    if (e.key === 'Tab') {
      setAberto(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!aberto) {
        setAberto(true);
        return;
      }
      const passo = e.key === 'ArrowDown' ? 1 : -1;
      setIndiceFoco((i) => {
        const proximo = i + passo;
        if (proximo < 0) return opcoes.length - 1;
        if (proximo >= opcoes.length) return 0;
        return proximo;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!aberto) {
        setAberto(true);
        return;
      }
      if (opcoes.length > 0) escolher(opcoes[Math.min(indiceFoco, opcoes.length - 1)]);
    }
  }

  return (
    <div ref={raiz} className="relative flex items-center gap-1.5">
      <span className="text-[12px] text-faint">Regional</span>

      <div className="relative">
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          {/* Lupa: e ela que diz "aqui se digita para procurar". Sem o icone, o
              campo parece um select comum e ninguem tenta digitar. */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
            <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>

        <input
          ref={campo}
          role="combobox"
          aria-expanded={aberto}
          aria-autocomplete="list"
          aria-label="Filtrar por regional"
          // Aberto: mostra o que se digita. Fechado: mostra o filtro em vigor.
          value={aberto ? busca : escolhida?.nome ?? 'Todas as regionais'}
          onChange={(e) => {
            setBusca(e.target.value);
            setAberto(true);
          }}
          onFocus={() => {
            setAberto(true);
            setBusca('');
          }}
          onKeyDown={aoTeclar}
          className={cn(
            'h-9 w-[220px] rounded-lg border border-line bg-surface pl-8 pr-7 text-[12px] font-semibold text-ink outline-none placeholder:font-normal placeholder:text-faint focus:border-primary',
            !escolhida && !aberto && 'text-muted-foreground'
          )}
          placeholder="Buscar regional"
        />

        {/* Limpar aparece so quando ha filtro: botao que nao faz nada e ruido. */}
        {valor !== null && !aberto && (
          <button
            type="button"
            onClick={() => escolher(null)}
            aria-label="Limpar filtro de regional"
            title="Voltar para todas as regionais"
            className="absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-[12px] text-faint hover:bg-soft hover:text-ink"
          >
            ✕
          </button>
        )}

        {aberto && (
          <div
            role="listbox"
            className="absolute left-0 top-[calc(100%+4px)] z-30 max-h-[320px] w-[280px] overflow-y-auto rounded-lg border border-line bg-surface text-ink shadow-card"
          >
            {opcoes.length === 0 && (
              <div className="px-3 py-2.5 text-[12px] text-faint">Nenhuma regional com esse nome.</div>
            )}

            {opcoes.map((r, i) => {
              const marcada = r === null ? valor === null : Number(r.codigo) === Number(valor);
              return (
                <button
                  key={r === null ? 'todas' : r.codigo}
                  type="button"
                  role="option"
                  aria-selected={marcada}
                  // `onMouseDown` e nao `onClick`: o clique precisa acontecer antes
                  // do blur do campo, que fecharia a lista antes de escolher.
                  onMouseDown={() => escolher(r)}
                  onMouseEnter={() => setIndiceFoco(i)}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-[12px] last:border-0',
                    i === indiceFoco && 'bg-soft',
                    marcada && 'font-bold text-primary'
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{r === null ? 'Todas as regionais' : r.nome}</span>
                  {marcada && <span className="flex-none text-[11px]">✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
